import { useSupportingPaneOpen } from "./useSupportingPaneOpen";
import { SupportingPaneProps } from "./SupportingPaneProps";
import {
  useResolveSupportingPanelSizes,
  UseResolveSupportingPanelSizesReturnValue,
} from "./useResolveSupportingPanelSizes";
import { ResizePanelProps } from "./ResizePanelProps";
import { ImperativePanelHandle } from "react-resizable-panels";
import { RefObject } from "react";

export interface UseSupportingPanelReturnValue
  extends UseResolveSupportingPanelSizesReturnValue {
  supportingPaneOpen: boolean;
  supportingPanelOnCollapse: () => void;
  supportingPanelOnExpand: () => void;
  supportingPanelOnToggleOpen: (open: boolean) => void;
  resizeHandleOnOpen: () => void;
}
export const useSupportingPanel = (props: {
  $key: string;
  supportPanelRef: RefObject<ImperativePanelHandle>;
  supportingPaneProps?: SupportingPaneProps;
  direction: "vertical" | "horizontal";
  height: number | undefined;
  width: number | undefined;
  resizePanelProps: ResizePanelProps | undefined;
}): UseSupportingPanelReturnValue => {
  const [supportingPaneOpen, setSupportingPaneOpen] =
    useSupportingPaneOpen(props);

  const sizes = useResolveSupportingPanelSizes({
    direction: props.direction,
    supportingPaneOpen: supportingPaneOpen,
    width: props.width,
    height: props.height,
    resizePanelProps: props.resizePanelProps,
  });

  const handleSupportingPanelExpand = () => {
    setSupportingPaneOpen(true);
  };

  const handleSupportingPanelCollapse = () => {
    setSupportingPaneOpen(false);
  };

  const handleResizeHandleOpen = () => {
    const supportingPanel = props.supportPanelRef.current;
    if (supportingPanel != null) {
      supportingPanel.expand();
      if (sizes.supportingPanelDefaultSize != null) {
        supportingPanel.resize(sizes.supportingPanelDefaultSize);
      }
    }
    setSupportingPaneOpen(true);
  };

  const handleSupportingPaneToggleOpen = (open: boolean) => {
    if (open !== supportingPaneOpen) {
      const supportingPanel = props.supportPanelRef.current;
      if (supportingPanel != null) {
        if (open) {
          supportingPanel.expand();
          if (sizes.supportingPanelMinSize != null) {
            supportingPanel.resize(sizes.supportingPanelMinSize);
          }
        } else {
          supportingPanel.collapse();
          supportingPanel.resize(0);
        }
      }

      setSupportingPaneOpen(open);
    }
  };

  return {
    supportingPaneOpen: supportingPaneOpen,
    supportingPanelOnCollapse: handleSupportingPanelCollapse,
    supportingPanelOnExpand: handleSupportingPanelExpand,
    resizeHandleOnOpen: handleResizeHandleOpen,
    supportingPanelOnToggleOpen: handleSupportingPaneToggleOpen,
    ...sizes,
  };
};
